import {useEffect, useRef, useState} from "react";
import {useGoogleOneTapLogin} from "@react-oauth/google";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ViewType} from "../../view_type/ViewType";
import {authSucceeded, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {googleOneTapLogin} from "../api/google_one_tap_login";
import {IStartRegistrationFlowPayload} from "../state/registration_flow_slice";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../tracking/auth_analytics";
import {getAmplitudeAuthLocation, trackAmplitudeAuthEvent} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";

interface IProps {
    disable?: boolean;
    gtmSourceSection?: string;
    userSource?: UserSource;
}

const ONE_TAP_DELAY_MS = 20000;

type RegistrationFlowState = IRootState["registrationFlow"];
type PropertyDetail = IRootState["property"]["property"];

const snapshotRegistrationFlow = (flow: RegistrationFlowState): IStartRegistrationFlowPayload => ({
    isUserLoggedIn: flow.isUserLoggedIn,
    hasCompleteProfile: flow.hasCompleteProfile,
    hasPhoneVerified: flow.hasPhoneVerified,
    registrationMethod: flow.registrationMethod,
    socialMediaType: flow.socialMediaType,
    userSource: flow.userSource,
    gtmSourceSection: flow.gtmSourceSection,
    gtmClickText: flow.gtmClickText,
    modalVariant: flow.modalVariant,
    propertyId: flow.propertyId,
    offerId: flow.offerId,
    vendorId: flow.vendorId,
    titleVariant: flow.titleVariant,
    showLoginForm: flow.showLoginForm,
    registrationSuccessCallback: flow.registrationSuccessCallback,
    useLeadAfterRegistration: flow.useLeadAfterRegistration,
    leadSource: flow.leadSource,
    leadSourceSection: flow.leadSourceSection,
    amplitudePlacement: flow.amplitudePlacement,
    amplitudeLocation: flow.amplitudeLocation
});

export const getGoogleOneTapPostAuthFlowContext = (
    registrationFlow: RegistrationFlowState,
    viewType: ViewType | null,
    property: PropertyDetail
): IPostAuthFlowContext | undefined => {
    if (registrationFlow.isActive) {
        return {
            flowWasActive: true,
            currentStep: registrationFlow.currentStep,
            registrationFlow: snapshotRegistrationFlow(registrationFlow)
        };
    }

    if (viewType !== ViewType.PROPERTY || !property?.id || !property.offer.id || !property.offer.vendor.id) {
        return undefined;
    }

    return {
        flowWasActive: false,
        registrationFlow: {
            userSource: UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP,
            modalVariant: "hidden_prices_variant_b",
            propertyId: property.id,
            offerId: property.offer.id,
            vendorId: property.offer.vendor.id,
            useLeadAfterRegistration: true,
            leadSource: ApplicationSource.PropertyRevealPrice,
            leadSourceSection: ApplicationSourceSection.MODAL
        }
    };
};

export const useGoogleOneTap = (props: IProps = {}) => {
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useAppDispatch();
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const property = useAppSelector((state) => state.property.property);
    const amplitudeLocation = getAmplitudeAuthLocation(viewType);
    const [readyToShow, setReadyToShow] = useState(false);
    const loginInFlightRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => setReadyToShow(true), ONE_TAP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const onOneTapLoginSuccess = async (isNewUser: boolean, postAuthFlowContext?: IPostAuthFlowContext) => {
        const userSource = props.userSource ?? UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP;
        const gtmSourceSection = postAuthFlowContext?.registrationFlow.gtmSourceSection ?? props.gtmSourceSection;

        authAnalytics.gtm.googleAuthEvent({
            event: isNewUser ? GAAuthEventName.SIGN_UP : GAAuthEventName.LOGIN,
            eventCategory: GAAuthEventCategory.GOOGLE_ONE_TAP,
            source: gtmSourceSection ?? null,
            userSource
        });

        if (!isNewUser) {
            trackAmplitudeAuthEvent("logged_in", {loginMethod: "google_popup", loginPlacement: "google_popup", location: amplitudeLocation});
        } else {
            trackAmplitudeAuthEvent("signed_up", {signUpMethod: "google_popup", signUpPlacement: "google_popup", location: amplitudeLocation});
        }

        dispatch(
            authSucceeded({
                type: isNewUser ? AuthActionType.REGISTRATION : AuthActionType.LOGIN,
                userSource,
                gtmSourceSection,
                registrationMethod: "social",
                socialMediaType: "google",
                authOrigin: "google_one_tap",
                postAuthFlowContext
            })
        );
    };

    const googleOnOneTapLoginError = (error?: unknown) => {
        loginInFlightRef.current = false;
        notifyBugsnag({message: `One tap login error`}, `third-party-login error`, {originalError: error});

        dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
    };

    useGoogleOneTapLogin({
        onSuccess: async (response) => {
            if (!response.credential || loginInFlightRef.current) {
                return;
            }

            loginInFlightRef.current = true;
            trackAmplitudeAuthEvent("login_click", {loginMethod: "google_popup", loginPlacement: "google_popup", location: amplitudeLocation});
            const postAuthFlowContext = getGoogleOneTapPostAuthFlowContext(registrationFlow, viewType, property);

            try {
                await googleOneTapLogin({
                    credential: response.credential,
                    onSuccess: (isNewUser) => onOneTapLoginSuccess(isNewUser, postAuthFlowContext),
                    onError: googleOnOneTapLoginError
                });
            } catch (error) {
                googleOnOneTapLoginError(error);
            }
        },
        onError: () => {
            googleOnOneTapLoginError("useGoogleOneTapLogin error, login in failure");
        },
        disabled: isAuthenticated || props.disable || !readyToShow
    });
};
import {useEffect, useRef, useState} from "react";
import {useGoogleOneTapLogin} from "@react-oauth/google";

import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {ViewType} from "../../view_type/ViewType";
import {authSucceeded, IPostAuthFlowContext} from "../actions/auth_succeeded_event";
import {googleOneTapLogin} from "../api/google_one_tap_login";
import {IStartRegistrationFlowPayload} from "../state/registration_flow_slice";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../tracking/auth_analytics";
import {getAmplitudeAuthLocation, trackAmplitudeAuthEvent} from "../tracking/track_amplitude_auth_event";
import {AuthActionType} from "../types/AuthActionType";

interface IProps {
    disable?: boolean;
    gtmSourceSection?: string;
    userSource?: UserSource;
}

const ONE_TAP_DELAY_MS = 20000;

type RegistrationFlowState = IRootState["registrationFlow"];
type PropertyDetail = IRootState["property"]["property"];

const snapshotRegistrationFlow = (flow: RegistrationFlowState): IStartRegistrationFlowPayload => ({
    isUserLoggedIn: flow.isUserLoggedIn,
    hasCompleteProfile: flow.hasCompleteProfile,
    hasPhoneVerified: flow.hasPhoneVerified,
    registrationMethod: flow.registrationMethod,
    socialMediaType: flow.socialMediaType,
    userSource: flow.userSource,
    gtmSourceSection: flow.gtmSourceSection,
    gtmClickText: flow.gtmClickText,
    modalVariant: flow.modalVariant,
    propertyId: flow.propertyId,
    offerId: flow.offerId,
    vendorId: flow.vendorId,
    titleVariant: flow.titleVariant,
    showLoginForm: flow.showLoginForm,
    registrationSuccessCallback: flow.registrationSuccessCallback,
    useLeadAfterRegistration: flow.useLeadAfterRegistration,
    leadSource: flow.leadSource,
    leadSourceSection: flow.leadSourceSection,
    amplitudePlacement: flow.amplitudePlacement,
    amplitudeLocation: flow.amplitudeLocation
});

export const getGoogleOneTapPostAuthFlowContext = (
    registrationFlow: RegistrationFlowState,
    viewType: ViewType | null,
    property: PropertyDetail
): IPostAuthFlowContext | undefined => {
    if (registrationFlow.isActive) {
        return {
            flowWasActive: true,
            currentStep: registrationFlow.currentStep,
            registrationFlow: snapshotRegistrationFlow(registrationFlow)
        };
    }

    if (viewType !== ViewType.PROPERTY || !property?.id || !property.offer.id || !property.offer.vendor.id) {
        return undefined;
    }

    return {
        flowWasActive: false,
        registrationFlow: {
            userSource: UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP,
            modalVariant: "hidden_prices_variant_b",
            propertyId: property.id,
            offerId: property.offer.id,
            vendorId: property.offer.vendor.id,
            useLeadAfterRegistration: true,
            leadSource: ApplicationSource.PropertyRevealPrice,
            leadSourceSection: ApplicationSourceSection.MODAL
        }
    };
};

export const useGoogleOneTap = (props: IProps = {}) => {
    const isAuthenticated = useIsAuthenticated();
    const dispatch = useAppDispatch();
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const viewType = useAppSelector((state) => state.viewType.current);
    const property = useAppSelector((state) => state.property.property);
    const amplitudeLocation = getAmplitudeAuthLocation(viewType);
    const [readyToShow, setReadyToShow] = useState(false);
    const loginInFlightRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => setReadyToShow(true), ONE_TAP_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const onOneTapLoginSuccess = async (isNewUser: boolean, postAuthFlowContext?: IPostAuthFlowContext) => {
        const userSource = props.userSource ?? UserSource.REGISTRATION_GOOGLE_ONE_TAP_RP;
        const gtmSourceSection = postAuthFlowContext?.registrationFlow.gtmSourceSection ?? props.gtmSourceSection;

        authAnalytics.gtm.googleAuthEvent({
            event: isNewUser ? GAAuthEventName.SIGN_UP : GAAuthEventName.LOGIN,
            eventCategory: GAAuthEventCategory.GOOGLE_ONE_TAP,
            source: gtmSourceSection ?? null,
            userSource
        });

        if (!isNewUser) {
            trackAmplitudeAuthEvent("logged_in", {loginMethod: "google_popup", loginPlacement: "google_popup", location: amplitudeLocation});
        } else {
            trackAmplitudeAuthEvent("signed_up", {signUpMethod: "google_popup", signUpPlacement: "google_popup", location: amplitudeLocation});
        }

        dispatch(
            authSucceeded({
                type: isNewUser ? AuthActionType.REGISTRATION : AuthActionType.LOGIN,
                userSource,
                gtmSourceSection,
                registrationMethod: "social",
                socialMediaType: "google",
                authOrigin: "google_one_tap",
                postAuthFlowContext
            })
        );
    };

    const googleOnOneTapLoginError = (error?: unknown) => {
        loginInFlightRef.current = false;
        notifyBugsnag({message: `One tap login error`}, `third-party-login error`, {originalError: error});

        dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
    };

    useGoogleOneTapLogin({
        onSuccess: async (response) => {
            if (!response.credential || loginInFlightRef.current) {
                return;
            }

            loginInFlightRef.current = true;
            trackAmplitudeAuthEvent("login_click", {loginMethod: "google_popup", loginPlacement: "google_popup", location: amplitudeLocation});
            const postAuthFlowContext = getGoogleOneTapPostAuthFlowContext(registrationFlow, viewType, property);

            try {
                await googleOneTapLogin({
                    credential: response.credential,
                    onSuccess: (isNewUser) => onOneTapLoginSuccess(isNewUser, postAuthFlowContext),
                    onError: googleOnOneTapLoginError
                });
            } catch (error) {
                googleOnOneTapLoginError(error);
            }
        },
        onError: () => {
            googleOnOneTapLoginError("useGoogleOneTapLogin error, login in failure");
        },
        disabled: isAuthenticated || props.disable || !readyToShow
    });
};
